import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from 'urql';
import { ALERTAS_DASHBOARD, RESOLVER_ALERTA } from '@/graphql/trazabilidadAlertas.gql.js';
import { useAuth } from '@/state/auth/AuthContext.jsx';

const SEVERIDADES = ['CRIT', 'WARN', 'INFO'];

export default function AlertasOperaciones() {
  const { sucursal } = useAuth();
  const sucursalId = Number(sucursal?.id || 0);

  const [filtroSeveridad, setFiltroSeveridad] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [referencia, setReferencia] = useState('');

  const [{ data, fetching, error }, reexecute] = useQuery({
    query: ALERTAS_DASHBOARD,
    variables: { sucursal_id: sucursalId, limite: 100 },
    pause: !sucursalId,
    requestPolicy: 'network-only',
  });
  const [, resolver] = useMutation(RESOLVER_ALERTA);

  const rows = useMemo(
    () =>
      (data?.alertasDashboard || []).filter(
        a =>
          (!filtroSeveridad || a.severidad === filtroSeveridad) &&
          (!filtroTipo || a.tipo.includes(filtroTipo)) &&
          (!referencia || String(a.referencia_id).includes(referencia))
      ),
    [data, filtroSeveridad, filtroTipo, referencia]
  );

  const counts = useMemo(
    () => rows.reduce((acc, r) => ({ ...acc, [r.severidad]: (acc[r.severidad] || 0) + 1 }), {}),
    [rows]
  );

  const getRefLink = item => {
    if (item.referencia_tipo === 'GUIA') return `/guias/${item.referencia_id}`;
    if (item.referencia_tipo === 'FOJA') return `/fojas/recepcion/${item.referencia_id}`;
    if (item.referencia_tipo === 'MINUTA') return `/cobranzas/minuta/${item.referencia_id}`;
    return null;
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-4 px-4 py-4">
      <header className="rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] p-4">
        <h1 className="text-2xl font-semibold text-[rgb(var(--fg))]">Alertas inteligentes</h1>
      </header>

      <section className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {SEVERIDADES.map(severidad => (
          <article
            key={severidad}
            className="rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] p-4"
          >
            <p className="text-xs text-[rgb(var(--muted))]">{severidad}</p>
            <p className="text-2xl font-semibold">{counts[severidad] || 0}</p>
          </article>
        ))}
      </section>

      <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <select
            className="rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--subpanel))] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
            value={filtroSeveridad}
            onChange={e => setFiltroSeveridad(e.target.value)}
          >
            <option value="">Todas severidades</option>
            {SEVERIDADES.map(item => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
          <input
            placeholder="Tipo"
            className="rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--subpanel))] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
            value={filtroTipo}
            onChange={e => setFiltroTipo(e.target.value)}
          />
          <input
            placeholder="Referencia ID"
            className="rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--subpanel))] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
            value={referencia}
            onChange={e => setReferencia(e.target.value)}
          />
          <button
            className="rounded-xl border border-[rgb(var(--border))] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
            onClick={() => reexecute({ requestPolicy: 'network-only' })}
          >
            Actualizar
          </button>
        </div>

        {fetching ? <p className="mt-3 text-sm">Cargando...</p> : null}
        {error ? <p className="mt-3 text-sm">Error: {error.message}</p> : null}

        <div className="mt-4 overflow-auto rounded-2xl border border-[rgb(var(--border))]">
          <table className="min-w-full text-sm">
            <thead className="bg-[rgb(var(--subpanel))]">
              <tr>
                <th className="px-3 py-2 text-left">Severidad</th>
                <th className="text-left">Tipo</th>
                <th className="text-left">Mensaje</th>
                <th className="text-left">Referencia</th>
                <th className="px-3 py-2 text-right">Acción</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(item => (
                <tr
                  key={`${item.tipo}-${item.referencia_tipo}-${item.referencia_id}`}
                  className="border-t border-[rgb(var(--border))]"
                >
                  <td className="px-3 py-2">{item.severidad}</td>
                  <td>{item.tipo}</td>
                  <td>{item.mensaje}</td>
                  <td>
                    {getRefLink(item) ? (
                      <Link className="underline" to={getRefLink(item)}>
                        {item.referencia_tipo} #{item.referencia_id}
                      </Link>
                    ) : (
                      `${item.referencia_tipo} #${item.referencia_id}`
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {item.id ? (
                      <button
                        className="rounded-lg border border-[rgb(var(--border))] px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                        onClick={async () => {
                          await resolver({ input: { id: Number(item.id), motivo: 'Resuelta desde panel', usuario: 'web' } });
                          reexecute({ requestPolicy: 'network-only' });
                        }}
                      >
                        Resolver
                      </button>
                    ) : (
                      <span className="text-[rgb(var(--muted))]">No persistida</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
